import { useEffect, useState, useMemo } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import JsonLd from '../../components/feature/JsonLd';

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('');
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    document.title = 'BPO Blog – AI Call Center Insights & Industry Trends | Call Center Pros';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Expert BPO blog insights: AI automation, nearshore call center trends, workforce management and customer service strategies from Call Center Pros industry leaders.');
  }, []);

  const blogSchema = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'BPO Insights & Resources – Call Center Pros',
    url: `${siteUrl}/bpo-blog`,
    description: 'Expert insights on AI-powered BPO, nearshore call center solutions, and customer service innovation.',
    publisher: {
      '@type': 'Organization',
      name: 'Call Center Pros',
      url: siteUrl,
    },
    blogPost: [
      {
        '@type': 'BlogPosting',
        headline: 'Unleashing the Power of Lead & Customer Data Scoring',
        datePublished: '2024-08-28',
        author: { '@type': 'Organization', name: 'Call Center Pros' },
        url: `${siteUrl}/unleashing-the-power-of-lead-scoring`,
      },
      {
        '@type': 'BlogPosting',
        headline: 'Will AI-powered digital agents replace call center jobs?',
        datePublished: '2024-08-23',
        author: { '@type': 'Organization', name: 'Call Center Pros' },
        url: `${siteUrl}/will-ai-powered-digital-agents-replace-call-center-jobs`,
      },
      {
        '@type': 'BlogPosting',
        headline: 'FCC 1 to 1 Consent Rule',
        datePublished: '2024-08-05',
        author: { '@type': 'Organization', name: 'Call Center Pros' },
        url: `${siteUrl}/fcc-1-to-1-consent-rule`,
      },
    ],
  }), [siteUrl]);

  const blogPosts = [
    {
      title: "Unleashing the Power of Lead & Customer Data Scoring",
      excerpt: "What is Lead Scoring? Lead scoring is a data-driven approach that allows businesses to evaluate and rank potential customers based...",
      date: "August 28, 2024",
      image: "https://public.readdy.ai/ai/img_res/caecca795b627b52f0a55dd7bf06864b.jpg",
      slug: "/unleashing-the-power-of-lead-scoring",
      category: "technology"
    },
    {
      title: "Will AI-powered digital agents replace call center jobs?",
      excerpt: "Call Center Pros CEO Jared Schagrin revealed AI agents will soon take over the call center industry. Here is everything...",
      date: "August 23, 2024",
      image: "https://readdy.ai/api/search-image?query=futuristic%20AI%20robot%20assistant%20with%20holographic%20interface%20working%20alongside%20human%20customer%20service%20representative%20in%20modern%20call%20center%20with%20blue%20digital%20elements%20sleek%20technology%20design&width=800&height=500&seq=blog2&orientation=landscape",
      slug: "/will-ai-powered-digital-agents-replace-call-center-jobs",
      category: "technology"
    },
    {
      title: "FCC 1 to 1 Consent Rule",
      excerpt: "The Federal Communications Commission (FCC) has recently implemented a new regulation known as the '1:1 Consent Rule,' which carries significant...",
      date: "August 5, 2024",
      image: "https://readdy.ai/api/search-image?query=professional%20legal%20compliance%20document%20with%20FCC%20regulations%20and%20consent%20forms%20on%20modern%20desk%20with%20gavel%20and%20digital%20tablet%20showing%20regulatory%20framework%20clean%20corporate%20setting&width=800&height=500&seq=blog3&orientation=landscape",
      slug: "/fcc-1-to-1-consent-rule",
      category: "trends"
    },
    {
      title: "Remote Customer Service Jobs on the Rise - 6 New Trends for Virtual Support",
      excerpt: "Remote customer service jobs are increasingly popular. Learn why they're gaining traction, how to source top remote customer support talent...",
      date: "November 18, 2023",
      image: "https://readdy.ai/api/search-image?query=remote%20work%20setup%20with%20professional%20customer%20service%20agent%20wearing%20headset%20working%20from%20home%20office%20with%20laptop%20video%20call%20interface%20modern%20workspace%20with%20plants%20natural%20lighting&width=800&height=500&seq=blog4&orientation=landscape",
      slug: "/remote-customer-service-jobs-rise",
      category: "workforce"
    },
    {
      title: "The Future of BPO: Trends Shaping 2024",
      excerpt: "Explore the key trends that are transforming the BPO industry in 2024, from AI integration to nearshore advantages...",
      date: "October 15, 2023",
      image: "https://readdy.ai/api/search-image?query=futuristic%20business%20process%20outsourcing%20concept%20with%20digital%20transformation%20elements%20global%20connectivity%20network%20holographic%20displays%20modern%20corporate%20environment%20blue%20technology%20theme&width=800&height=500&seq=blog5&orientation=landscape",
      slug: "/future-of-bpo-trends-2024",
      category: "trends"
    },
    {
      title: "Maximizing ROI with Nearshore Call Center Solutions",
      excerpt: "Discover how nearshore call center solutions can significantly improve your ROI while maintaining high-quality customer service...",
      date: "September 28, 2023",
      image: "https://readdy.ai/api/search-image?query=modern%20nearshore%20call%20center%20facility%20with%20professional%20agents%20at%20workstations%20financial%20growth%20charts%20ROI%20metrics%20displayed%20on%20screens%20contemporary%20office%20design%20with%20blue%20accents&width=800&height=500&seq=blog6&orientation=landscape",
      slug: "/maximizing-roi-nearshore-solutions",
      category: "callcenter"
    }
  ];

  const categories = [
    { id: 'all', name: 'All Articles', icon: 'ri-article-line', count: blogPosts.length },
    { id: 'callcenter', name: 'Call Center', icon: 'ri-phone-line', count: blogPosts.filter(p => p.category === 'callcenter').length },
    { id: 'technology', name: 'AI & Technology', icon: 'ri-robot-line', count: blogPosts.filter(p => p.category === 'technology').length },
    { id: 'workforce', name: 'Workforce', icon: 'ri-team-line', count: blogPosts.filter(p => p.category === 'workforce').length },
    { id: 'trends', name: 'Industry Trends', icon: 'ri-line-chart-line', count: blogPosts.filter(p => p.category === 'trends').length }
  ];

  const filteredPosts = selectedCategory === 'all' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  const featuredPost = blogPosts[0];

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newsletterEmail) {
      setNewsletterStatus('Please enter your email address');
      return;
    }

    try {
      const formData = new URLSearchParams();
      formData.append('email', newsletterEmail);

      const response = await fetch('https://readdy.ai/api/form/d6s22t5sbgrc47fimqhg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString()
      });

      if (response.ok) {
        setNewsletterStatus('Thank you for subscribing!');
        setNewsletterEmail('');
      } else {
        setNewsletterStatus('Something went wrong. Please try again.');
      }
    } catch (error) {
      setNewsletterStatus('Something went wrong. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <JsonLd id="blog-page-schema" data={blogSchema} />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 py-24">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">BPO Insights & Resources</h1>
            <p className="text-xl text-blue-100 mb-8">Expert insights, industry trends, and actionable strategies to transform your business operations</p>
            <div className="flex flex-wrap justify-center gap-6 text-white">
              <div className="flex items-center gap-2">
                <i className="ri-article-line text-2xl"></i>
                <span className="text-sm font-medium">50+ Articles</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-user-line text-2xl"></i>
                <span className="text-sm font-medium">Industry Experts</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-2xl"></i>
                <span className="text-sm font-medium">Weekly Updates</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-gray-50 border-b border-gray-200 sticky top-0 z-40">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-5 py-2.5 rounded-full font-medium text-sm transition-all whitespace-nowrap ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <i className={`${category.icon} mr-2`}></i>
                {category.name} ({category.count})
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Post */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="mb-6">
              <span className="inline-block px-4 py-1.5 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full">Featured Article</span>
            </div>
            <div className="bg-white rounded-2xl overflow-hidden shadow-xl border border-gray-100 hover:shadow-2xl transition-shadow">
              <div className="md:flex">
                <div className="md:w-1/2">
                  <div className="relative h-64 md:h-full w-full">
                    <img src={featuredPost.image} alt={featuredPost.title} className="w-full h-full object-cover object-top" />
                  </div>
                </div>
                <div className="md:w-1/2 p-8 md:p-12 flex flex-col justify-center">
                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i>
                      {featuredPost.date}
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line"></i>
                      5 min read
                    </span>
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4 leading-tight">{featuredPost.title}</h2>
                  <p className="text-gray-600 mb-6 text-lg leading-relaxed">{featuredPost.excerpt}</p>
                  <div>
                    <a 
                      href={featuredPost.slug} 
                      className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors whitespace-nowrap"
                    >
                      Read Full Article
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {selectedCategory === 'all' ? 'Latest Articles' : `${categories.find(c => c.id === selectedCategory)?.name} Articles`}
            </h2>
            <p className="text-gray-600 text-lg">Stay updated with the latest trends and insights in BPO</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {filteredPosts.slice(1).map((post, index) => (
              <article key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all hover:-translate-y-1 border border-gray-100">
                <div className="relative h-48 w-full">
                  <img src={post.image} alt={post.title} className="w-full h-full object-cover object-top" />
                  <div className="absolute top-4 right-4">
                    <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-blue-600 text-xs font-semibold rounded-full">
                      {categories.find(c => c.id === post.category)?.name}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                    <i className="ri-calendar-line"></i>
                    <span>{post.date}</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 leading-tight">{post.title}</h3>
                  <p className="text-gray-600 mb-4 line-clamp-3 text-sm leading-relaxed">{post.excerpt}</p>
                  <a 
                    href={post.slug} 
                    className="inline-flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-800 transition-colors text-sm whitespace-nowrap"
                  >
                    Read More
                    <i className="ri-arrow-right-line"></i>
                  </a>
                </div>
              </article>
            ))}
          </div>

          {/* Load More Button */}
          <div className="text-center mt-12">
            <button className="bg-gray-900 text-white px-8 py-3.5 rounded-lg font-semibold hover:bg-gray-800 transition-colors shadow-lg whitespace-nowrap">
              Load More Articles
            </button>
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-mail-line text-white text-3xl"></i>
            </div>
            <h2 className="text-4xl font-bold text-white mb-4">Stay Ahead of the Curve</h2>
            <p className="text-blue-100 mb-8 text-lg">Subscribe to our newsletter for the latest BPO insights, industry news, and exclusive resources delivered to your inbox</p>
            <form onSubmit={handleNewsletterSubmit} className="max-w-md mx-auto" id="newsletter-form" data-readdy-form>
              <div className="flex flex-col sm:flex-row gap-3">
                <input 
                  type="email" 
                  name="email"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  placeholder="Enter your email address" 
                  required
                  className="flex-1 px-5 py-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 text-gray-900 placeholder-gray-500"
                />
                <button 
                  type="submit"
                  className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-blue-50 transition-colors shadow-lg whitespace-nowrap"
                >
                  Subscribe Now
                </button>
              </div>
              {newsletterStatus && (
                <p className={`mt-4 text-sm ${newsletterStatus.includes('Thank you') ? 'text-green-200' : 'text-yellow-200'}`}>
                  {newsletterStatus}
                </p>
              )}
            </form>
            <p className="text-blue-200 text-sm mt-4">Join 5,000+ professionals already subscribed</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;